import { Button, Card, CardBody, Flex, Icon, Stack, Text } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { FiArrowRight, FiEdit3, FiEye } from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import {
  PortalLifecycleStatus,
  BidV2Status,
  useCreateBidV2Mutation,
  useGetPortalMyBidsQuery,
  useGetPortalRegistrationStatusQuery,
  type PortalSourcingEventDetailDto,
} from "../../../../autogen/bff-api";
import { useApiError } from "../../../../common/errors/useApiError";
import { useAppSelector } from "../../../../common/redux/hooks";
import { isDpsCompetition } from "../../buyer/wizard/utils/procedure";
import { usePortalEventUrls } from "../usePortalEventUrls";
import { isPrivatePortalEvent } from "../view-single/PrivateInviteCta";
import { isReadonlyBidStatus } from "./readonly/readonlyBidStatus";

export const StartBidCta = ({ event }: { event: PortalSourcingEventDetailDto }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const portalUrls = usePortalEventUrls();
  const displayer = useApiError();
  const authState = useAppSelector((state) => state.authState.state);
  const [createBid, { isLoading }] = useCreateBidV2Mutation();

  const isLoggedInWithOrg = authState?.type === "LoggedInWithOrgContext";
  const isOpen = event.lifecycle.status === PortalLifecycleStatus.Open;
  const privateEvent = isPrivatePortalEvent(event);

  // The purchase is served only to the suppliers it invited (§ 26-7), so seeing it is the ticket.
  const invitedByScheme = isDpsCompetition(event.procedureType);

  const { data: myBids } = useGetPortalMyBidsQuery({}, { skip: !isLoggedInWithOrg });
  const ownBid = myBids?.bids.find((bid) => bid.eventId === event.id);

  // Registering interest is the entry point; the offer CTA only appears once the supplier is following the procurement.
  const { data: status } = useGetPortalRegistrationStatusQuery(
    { eventId: event.id },
    { skip: !isLoggedInWithOrg || !isOpen || !!event.competitionStage || privateEvent || !!ownBid }
  );

  if (!isLoggedInWithOrg) return null;

  const openBid = (bidId: string) =>
    navigate(
      privateEvent ? portalUrls.privateBid.edit.go(event.id, bidId) : portalUrls.bid.edit.go(event.id, bidId).intro,
    );

  if (ownBid) {
    if (isOpen && (event.competitionStage || privateEvent)) return null;
    const cta = deriveCta(ownBid.status, isOpen, t);
    return <BidCard cta={cta} onClick={() => openBid(ownBid.bidId)} />;
  }

  // Two-stage competitions gate the offer on the invitation (ParticipationCta), not registration;
  // private procurements start bids from PrivateBidCta.
  if (!isOpen || event.competitionStage || privateEvent) return null;
  if (!status || (!invitedByScheme && !status.hasRegistered)) return null;

  const startNewBid = async () => {
    try {
      const bid = await createBid({ eventId: event.id }).unwrap();
      navigate(portalUrls.bid.edit.go(event.id, bid.id).intro);
    } catch (e) {
      displayer.trigger(e);
    }
  };

  return <BidCard cta={deriveCta(null, isOpen, t)} onClick={startNewBid} isLoading={isLoading} />;
};

const BidCard = ({
  cta,
  onClick,
  isLoading = false,
}: {
  cta: ReturnType<typeof deriveCta>;
  onClick: () => void;
  isLoading?: boolean;
}) => (
  <Card variant="filled" bg="smPrimarySofter" rounded="xl">
    <CardBody>
      <Flex direction={{ base: "column", md: "row" }} align={{ md: "center" }} justify="space-between" gap="4">
        <Stack spacing="1">
          <Text fontWeight="semibold">{cta.title}</Text>
          <Text fontSize="sm" color="smMuted">
            {cta.subtitle}
          </Text>
        </Stack>
        <Button colorScheme="teal" leftIcon={<Icon as={cta.icon} />} onClick={onClick} isLoading={isLoading} flexShrink={0}>
          {cta.button}
        </Button>
      </Flex>
    </CardBody>
  </Card>
);

const deriveCta = (ownBidStatus: BidV2Status | null, isOpen: boolean, t: (key: string) => string) => {
  if (ownBidStatus == null) {
    return {
      title: t("Want to compete for this contract?"),
      subtitle: t("Build your offer step by step. We save your progress automatically."),
      button: t("Start offer"),
      icon: FiEdit3,
    };
  }
  if (isReadonlyBidStatus(ownBidStatus)) {
    return {
      title: t("Your offer has been delivered"),
      subtitle: t("You can view the offer you submitted."),
      button: t("View offer"),
      icon: FiEye,
    };
  }
  if (!isOpen) {
    return {
      title: t("Your offer was not delivered"),
      subtitle: t("The deadline has passed. You can still open and read it."),
      button: t("View offer"),
      icon: FiEye,
    };
  }
  return {
    title: t("You have a saved offer"),
    subtitle: t("Continue where you left off — we save your progress automatically."),
    button: t("Continue offer"),
    icon: FiArrowRight,
  };
};
